/**
 * Renders a single chat message bubble.
 *
 * Distinguishes between USER_MESSAGE and SYSTEM_NOTE:
 * - USER_MESSAGE: displayed as a chat bubble with author, company, and timestamp.
 * - SYSTEM_NOTE: displayed as a centred italic label (no bubble).
 *
 * Renders all attachments (new multi-attachment API), with a legacy fallback for
 * messages that only carry the old single `attachment` field.
 *
 * For Operators and Admins (non-participants), the `requesterCompanyId` prop overrides
 * the default own-message detection so that messages authored by the requester appear
 * right-aligned (same visual convention as the Buyer's own view), while supplier
 * messages remain left-aligned.
 */

import React from "react";
import {Box, Link, Tooltip, Typography} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import {useTranslation} from "react-i18next";
import {NotificationI18N} from "@/translations";
import {triggerBlobDownload} from "@/lib/blob-download";
import {formatIsoDateTime} from "@/lib/utils";
import {useAuth} from "@/app/auth/AuthContext";
import {useSnackbarEx} from "@/lib/snackbarex";
import {downloadAttachmentBlob} from "./api/conversationApi";
import ReadByTooltip from "./ReadByTooltip";
import type {MessageTimelineEntry, NotificationAttachmentMeta, NotificationsChatMessage} from "./types";

interface MessageBubbleProps {
    requestId: number;
    chatId: number;
    entry: MessageTimelineEntry;
    /**
     * When provided (Operator/Admin non-participant view), overrides the default
     * own-message detection: a message is treated as "own" (right-aligned) when its
     * `authorCompany.id` matches this requester company id. This mirrors the visual
     * convention that the requester (Buyer) appears on the right side.
     *
     * When absent, the standard logic applies: a message is "own" when its
     * `authorCompany` matches the currently authenticated user's company.
     */
    requesterCompanyId?: number;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({requestId, chatId, entry, requesterCompanyId}) => {
    const {t, i18n} = useTranslation();
    const {company} = useAuth();
    const {showMessage} = useSnackbarEx();

    // Cast to the extended type since the generated ChatMessageDTO does not include
    // the notifications-specific fields (body, kind, authorCompany, createdAt, attachments).
    const msg = entry.message as unknown as NotificationsChatMessage;

    if (!msg) {
        return null;
    }

    const isSystemNote = msg.kind === "SYSTEM_NOTE";
    // For Operators/Admins (non-participants), `requesterCompanyId` is provided and overrides
    // the alignment logic: messages authored by the requester appear right-aligned, supplier
    // messages left-aligned. For Buyers/Suppliers the standard check applies (own company = right).
    const isOwn = requesterCompanyId === undefined
            ? company?.id !== undefined && msg.authorCompany?.id === company.id
            : msg.authorCompany?.id === requesterCompanyId;
    // The notifications API uses `body`; fall back to legacy `message` field
    const text = msg.body ?? msg.message ?? "";
    // Prefer notifications `createdAt`; fall back to legacy `timestamp`
    const timestamp = msg.createdAt ?? msg.timestamp;
    const authorName = msg.author
            ? [msg.author.firstName, msg.author.lastName].filter(Boolean).join(" ")
            : "";
    const companyName = msg.authorCompany?.name ?? "";

    const attachments: NotificationAttachmentMeta[] = msg.attachments ?? [];

    const handleDownload = async (attachment: NotificationAttachmentMeta) => {
        try {
            const blob = await downloadAttachmentBlob(requestId, chatId, attachment.id);
            triggerBlobDownload(blob, attachment.filename);
        } catch (error: unknown) {
            showMessage({
                severity: "error",
                summary: t(NotificationI18N.chat.attachment.downloadFailed),
                error,
            });
        }
    };

    if (isSystemNote) {
        return (
                <Box
                        sx={{
                            textAlign: "center",
                            my: 0.5,
                            color: "text.secondary",
                            fontStyle: "italic",
                        }}
                >
                    <Typography variant="caption">{text}</Typography>
                    {timestamp && (
                            <Typography variant="caption" sx={{ml: 1}}>
                                ({formatIsoDateTime(timestamp, i18n.language)})
                            </Typography>
                    )}
                </Box>
        );
    }

    return (
            <Box sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: isOwn ? "flex-end" : "flex-start",
                mb: 0.5
            }}>
                {/* Author + company + timestamp line */}
                <Box sx={{
                    display: "flex",
                    gap: 0.5,
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: isOwn ? "flex-end" : "flex-start"
                }}>
                    <Typography variant="caption" fontWeight="bold">
                        {authorName}
                    </Typography>
                    {companyName && (
                            <Typography variant="caption" color="text.secondary">
                                ({companyName})
                            </Typography>
                    )}
                    {timestamp && (
                            <Typography variant="caption" color="text.secondary">
                                {formatIsoDateTime(timestamp, i18n.language)}
                            </Typography>
                    )}
                    {!isOwn && <ReadByTooltip readBy={msg.readBy ?? []}/>}
                </Box>

                {/* Message bubble */}
                <Box
                        sx={{
                            bgcolor: isOwn ? "primary.main" : "grey.100",
                            color: isOwn ? "primary.contrastText" : "inherit",
                            borderRadius: 2,
                            px: 1.5,
                            py: 1,
                            maxWidth: "85%",
                            wordBreak: "break-word",
                        }}
                >
                    {text.trim().length > 0 && (
                            <Typography variant="body2"
                                        sx={{color: isOwn ? "inherit" : "text.primary"}}>{text}</Typography>
                    )}

                    {/* Attachments — one row per file */}
                    {attachments.map((att) => (
                            <Box key={att.id} sx={{mt: 0.5, display: "flex", alignItems: "center", gap: 0.5}}>
                                <AttachFileIcon
                                        fontSize="small" {...(isOwn ? {sx: {color: "inherit"}} : {color: "action" as const})} />
                                <Tooltip title={t(NotificationI18N.chat.attachment.download)}>
                                    <Link
                                            component="button"
                                            variant="caption"
                                            onClick={() => void handleDownload(att)}
                                            underline="hover"
                                            sx={{cursor: "pointer", ...(isOwn ? {color: "inherit"} : {})}}
                                    >
                                        {att.filename}
                                    </Link>
                                </Tooltip>
                            </Box>
                    ))}
                </Box>
            </Box>
    );
};

export default MessageBubble;
